<template>
  <div>
    <div
      v-if="!loading"
      class="is-flex is-flex-direction-column"
      style="gap: 1rem"
    >
      <div class="is-flex mt-2" style="gap: 1rem">
        <b-field label="Client(s)" class="report_config__select mb-0">
          <MultiSelect
            v-model="salesReportParams.clients"
            label="name"
            track-by="id"
            :options="clientOptions"
            :multiple="true"
            :taggable="true"
            tag-position="top"
            :max-height="200"
            :close-on-select="true"
            :clear-on-select="false"
            :preserve-search="true"
            :allow-empty="true"
            placeholder="Select a Client"
            @input="() => handleRequestsOptionsClientsUpdate(salesReportParams)"
          >
          </MultiSelect>
        </b-field>

        <b-field label="User(s)" class="report_config__select mb-0">
          <MultiSelect
            v-model="salesReportParams.users"
            label="name"
            track-by="id"
            :loading="isLoadingUsers"
            :options="userOptions"
            :multiple="true"
            :taggable="true"
            tag-position="top"
            :max-height="200"
            :close-on-select="true"
            :clear-on-select="false"
            :preserve-search="true"
            :allow-empty="true"
            placeholder="Select a User"
            @search-change="validateSearchingUsers"
            @input="() => handleRequestsOptionsUsersUpdate(salesReportParams)"
          >
          </MultiSelect>
        </b-field>
      </div>

      <div>
        <b-field label="Creation Range Date" class="range-date is-relative">
          <BaseDatePicker
            v-model="salesReportParams.from"
            placeholder="From"
            :min="salesReportParams.min_from"
            :max="salesReportParams.max_from"
            size="medium"
            class="request_date__field"
            :is-not-valid="false"
          />
          <BaseDatePicker
            v-model="salesReportParams.to"
            placeholder="To"
            :min="salesReportParams.min_from"
            :max="salesReportParams.max_from"
            size="medium"
            class="request_date__field ml-4"
            :is-not-valid="false"
          />
          <p
            style="
              position: absolute;
              text-decoration: underline;
              top: 0;
              right: 0;
              cursor: pointer;
            "
            @click="resetSalesReportFilters"
          >
            <span
              class="is-flex is-align-items-center"
              style="font-size: 0.8rem"
            >
              <!-- <b-icon icon="close" class="mr-2" custom-size="mdi-18px" /> -->

              Clear Filters
            </span>
          </p>
        </b-field>
      </div>

      <div>
        <BaseButton
          v-if="hasMetrics"
          ref="searchQuotesButton"
          size="large"
          action="outline-gray"
          class="mt-3"
          @click="() => loadReports(salesReportParams)"
        >
          <IconSearch slot="icon" class="icon is-small" />

          <p slot="text">Load Metrics</p>
        </BaseButton>

        <div class="is-flex align-content-center">
          <b-switch
            class="color-2 mr-0"
            value="true"
            style="width: 300px"
            @input="salesReportParams.only_with_request = $event"
          >
            <p class="color-7">Include only with requests</p>
          </b-switch>

          <BaseButton
            ref="searchQuotesButton"
            size="large"
            action="principal"
            class="w-100"
            :loading="downloading"
            @click="() => downloadSalesReport(salesReportParams)"
          >
            <b-icon slot="icon" icon="download" custom-size="mdi-18px" />

            <p slot="text">Download</p>
          </BaseButton>
        </div>
      </div>
    </div>
    <div v-else class="is-flex mt-4" style="flex-wrap: wrap; gap: 1rem">
      <div>
        <b-skeleton height="100px" width="820px" :active="true" />
      </div>

      <div>
        <b-skeleton height="300px" width="400px" :active="true" />
      </div>

      <div>
        <b-skeleton height="300px" width="400px" :active="true" />
      </div>

      <div>
        <b-skeleton height="300px" width="400px" :active="true" />
      </div>

      <div>
        <b-skeleton height="300px" width="400px" :active="true" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { USER_ROLE_IDS, DATE_FORMAT_YYYY_MM_DD } from "@/constants";
import BaseButton from "@/components/base-ui/BaseButton.vue";
import IconSearch from "@/components/icons/IconSearch.vue";
import { format } from "date-fns";
import MultiSelect from "vue-multiselect";
import reportService from "@/services/reports";
import excel from "@/utils/excel";

export default {
  name: "ReportFilter",
  components: { IconSearch, BaseButton, MultiSelect },
  props: {
    loadReports: {
      type: Function,
      default: () => {},
    },
    hasMetrics: {
      type: Boolean,
      default: false,
    },
    reportType: {
      type: String,
      required: true,
    },
    reportFilename: {
      type: String,
      required: true,
    },
  },
  data() {
    const min_from = format(new Date(2024, 0, 1), DATE_FORMAT_YYYY_MM_DD);

    const now = new Date();
    now.setDate(now.getDate() - 1);

    const max_from = format(now, DATE_FORMAT_YYYY_MM_DD);

    const clients = [{ id: "", name: "All Clients" }];

    const users = [{ id: "", name: "All Users" }];

    const usersText = "All Users";

    const clientsText = "All Clients";

    const defaultRequestsByClientOptionsParams = {
      from: min_from,
      to: max_from,
      loading: false,
      users,
      usersText,
      clients,
      clientsText,
    };

    return {
      downloading: false,
      isLoadingUsers: false,
      salesReportParams: {
        min_from: min_from,
        max_from: max_from,
        from: min_from,
        to: max_from,
        users,
        usersText,
        clients,
        clientsText,
        only_with_request: true,
      },
      requestsByTimeOptions: {
        responsive: true,
        plugins: {
          legend: {
            display: true,
          },
          title: {
            display: false,
            text: "Monthly Sales",
          },
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
        defaultParams: defaultRequestsByClientOptionsParams,
      },
      requestsByClientOptions: {
        responsive: true,
        plugins: {
          legend: {
            position: "bottom",
          },
          title: {
            display: false,
            text: "Doughnut Chart Example",
          },
        },
        defaultParams: defaultRequestsByClientOptionsParams,
      },
      requestsByDestinationOptions: {
        responsive: true,
        scales: {
          x: {
            beginAtZero: true, // Ensures bars start from zero
          },
        },
        plugins: {
          legend: {
            position: "bottom", // Positions the legend at the top
          },
          title: {
            display: false,
            text: "Requests by Destinations", // Title of the chart
          },
        },
        defaultParams: defaultRequestsByClientOptionsParams,
      },
      requestsByStageOptions: {
        responsive: true,
        scales: {
          x: {
            beginAtZero: true, // Ensures bars start from zero
          },
        },
        plugins: {
          legend: {
            position: "bottom", // Positions the legend at the top
          },
          title: {
            display: false,
            text: "Requests by Stage", // Title of the chart
          },
        },
        loading: false,
        defaultParams: defaultRequestsByClientOptionsParams,
      },
      loading: true,
      dateToStringOptions: {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      },
      selectedChart: null,
      selectedSourceData: null,
      selectedSourceDataColumns: null,
      isSourceDataModalOpen: false,
      areReportsloading: false,
    };
  },
  computed: {
    ...mapGetters({
      authData: "auth/getAuthData",
      requestsByTime: "reports/getQuotesByTime",
      requestsByClients: "reports/getQuotesByClients",
      requestsByStage: "reports/getRequestsByStages",
      requestsByDestinations: "reports/getRequestsByDestinations",
      userSummary: "reports/getUserSummary",
      clients: "client/getClients",
      authUser: "auth/getAuthUserData",
      users: "users/getUsers",
    }),
    clientOptions() {
      return [{ id: "", name: "All Clients" }, ...this.clients];
    },
    userOptions() {
      const defaultOptions = [
        { id: "", name: "All Users" },
        this.authData.user,
      ];

      const filteredOptions = this.users.data?.filter(
        (user) => user.id !== this.authData.user.id
      );

      return [...defaultOptions, ...filteredOptions];
    },
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    resetSalesReportFilters() {
      this.salesReportParams = {
        ...this.salesReportParams,
        from: this.salesReportParams.min_from,
        to: this.salesReportParams.max_from,
        clients: [{ id: "", name: "All Clients" }],
      };
    },
    handleRequestsOptionsClientsUpdate(params) {
      let clientsText = "All Clients";

      if (params.clients.length > 0) {
        clientsText = params.clients.map((client) => client.name).join(",");
      }

      params.clientsText = clientsText;
    },
    async loadUsers(payload = {}) {
      return await this.$store.dispatch("utils/validateFunctionExecution", {
        functionToExecute: async () => {
          this.isLoadingUsers = true;

          await this.$store.dispatch("users/getUsers", {
            page: 1,
            name: payload?.name || "",
            role: [USER_ROLE_IDS.ADMIN, USER_ROLE_IDS.TRAVEL_AGENT].join(","),
          });

          this.isLoadingUsers = false;
        },
        functionToExecuteOnFail: async (error) => {
          this.loading = false;
          this.isLoadingUsers = false;
          this.$buefy.toast.open({
            duration: 5000,
            message: "Error trying to get users, please try again.",
            position: "is-top",
            type: "is-danger",
          });
          throw error;
        },
      });
    },
    async validateSearchingUsers(event) {
      if (event.length > 3) {
        await this.loadUsers({ name: event });
      }
    },
    handleRequestsOptionsUsersUpdate(params) {
      let usersText = "All Users";

      if (params.users.length > 0) {
        usersText = params.users.map((user) => user.name).join(",");
      }

      params.usersText = usersText;
    },
    async loadClients() {
      return await this.$store.dispatch("utils/validateFunctionExecution", {
        functionToExecute: async () => {
          return await this.$store.dispatch("client/getClients", {
            page: 1,
          });
        },
        functionToExecuteOnFail: async (error) => {
          this.loading = false;
          this.$buefy.toast.open({
            duration: 5000,
            message: "Error trying to get clients, please try again.",
            position: "is-top",
            type: "is-danger",
          });
          throw error;
        },
      });
    },
    async downloadSalesReport(params) {
      this.setLoading(true);

      const adaptedParams = {
        client: params?.clients?.map((client) => client.id),
        from: params?.from,
        to: params?.to,
        user: params?.users?.map((user) => user.id),
        only_with_request: params.only_with_request,
      };

      try {
        const workbook = excel.newWorkbook();

        const detailResponse = await reportService.loadReport(
          this.reportType,
          adaptedParams
        );

        excel.addNewSheet(workbook, "DETAIL", detailResponse.data);

        if (this.hasMetrics) {
          const conversionResponse = await reportService.loadConversions(
            adaptedParams
          );

          excel.addNewSheet(workbook, "CONVERSIONS", conversionResponse.data);
        }

        const filename = `${this.reportFilename} - ${adaptedParams.from} to ${adaptedParams.to}`;

        await excel.download(workbook, filename);
      } catch (e) {
        console.error(e);
        this.$buefy.toast.open({
          duration: 5000,
          message:
            "Upps! There was an error downloading your report. Please try again.",
          position: "is-top",
          type: "is-danger",
        });
      }

      this.setLoading(false);
    },
    closeModal() {
      this.isSourceDataModalOpen = false;
    },
    async loadInitialData() {
      this.loading = true;
      await Promise.allSettled([
        this.loadReports(this.salesReportParams),
        this.loadClients(),
        this.loadUsers(),
      ]);
      this.loading = false;
    },
    setLoading(value = false) {
      this.downloading = value;
      this.$store.dispatch("utils/setLoading", value);
      if (!value) {
        this.setLoadingText("Loading content, please wait...");
      }
    },
    setLoadingText(value) {
      this.$store.dispatch("utils/setLoadingText", value);
    },
  },
};
</script>
